import React from 'react';
import { useTheme } from 'styled-components';
import { InfoCircleSvg } from 'src/assets/dynamicSvg';
import { Poppins } from './Poppins';
import * as S from './NoticeBar.styles';

interface NoticeBarProps {
  children: React.ReactNode;
  dataCy?: string;
  /** So a caller can place it — margins are the caller's business, never the bar's. */
  className?: string;
}

/**
 * The app's standing "here is why this surface is empty or incomplete" bar.
 *
 * One definition, three callers: the maturity spider's two warnings (CRPD-7274) and the hierarchy
 * tab's empty state (CRPD-7267). It was drawn three times before it was named once — the shape is
 * the maturity page's, promoted here verbatim so the existing surfaces do not shift a pixel.
 *
 * What it is NOT: `Alert` (a red-tinted destructive block), `MessageContainer`, or the maturity
 * page's dismissible `SourceFrameworkBanner`. This one is a statement of fact about the data —
 * nothing to acknowledge, nothing to dismiss, so it carries no control of its own.
 *
 * The disc is the app's existing `InfoCircleSvg` tinted `colors.error`, which is the design's
 * `Cydea/Red`. Nothing new was drawn for it.
 */
export const NoticeBar: React.FC<NoticeBarProps> = ({ children, dataCy, className }) => {
  const { colors } = useTheme();

  return (
    <S.Bar className={className} data-cy={dataCy}>
      <InfoCircleSvg size={16} color={colors.error} />
      <Poppins px={14}>{children}</Poppins>
    </S.Bar>
  );
};
